import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardStatSkeleton, DashboardChartSkeleton } from '@/features/admin/components/loaders/DashboardSkeletons';
import { StatCard } from './components/StatCard';
import { SectionCard } from './components/SectionCard';
import { ExtensionSetupGuide } from './components/ExtensionSetupGuide';
import { EmptyState } from './components/EmptyState';
import { SuperAdminDashboard } from './components/SuperAdminDashboard';
import { useDashboardData } from './hooks/useDashboardData';
import { formatDate } from '../../../../shared/utils/date';
import { useGetConnectionsQuery, useGetCrmStatusQuery } from '../../api/preCallApi';
import { useLazyListSessionsQuery } from '../../api/sessionsApi';
import { Dropdown } from '@/shared/components/atoms/Dropdown/Dropdown';
import { Button } from '@/shared/components/atoms/Button/Button';
import { AlertUtils } from '@/shared/utils/alert';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  PhoneCall,
  Clock,
  Activity,
  Zap,
  Database,
  CalendarDays,
  Mail,
  ArrowUpRight,
  BarChart3,
  Loader2,
  Video
} from 'lucide-react';

export function Dashboard() {
  const navigate = useNavigate();
  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');

  if (adminUser?.role === 'SuperAdmin') {
    return <SuperAdminDashboard />;
  }

  const getPlatformLogo = (platform: string) => {
    const p = (platform || '').toLowerCase();
    if (p.includes('zoom')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="24" height="24" rx="6" fill="var(--color-brand-zoom, #2D8CFF)" />
          <path d="M7 9C6.44772 9 6 9.44772 6 10V14C6 14.5523 6.44772 15 7 15H13C13.5523 15 14 14.5523 14 14V10C14 9.44772 13.5523 9 13 9H7Z" fill="var(--color-text-primary, white)" />
          <path d="M15 10.5L18.2 8.1C18.6418 7.76863 19 8.0834 19 8.6V15.4C19 15.9166 18.6418 16.2314 18.2 15.9L15 13.5V10.5Z" fill="var(--color-text-primary, white)" />
        </svg>
      );
    }
    if (p.includes('meet')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21.5 5.5L16 9.5V14.5L21.5 18.5V5.5Z" fill="var(--color-brand-meet-sec, #00832d)" />
          <rect x="3" y="5.5" width="13" height="13" rx="2" fill="var(--color-brand-meet, #0066da)" />
        </svg>
      );
    }
    return <Video size={14} color="var(--admin-card-subtext)" />;
  };
  const [days, setDays] = useState<number>(7);
  const [chartTab, setChartTab] = useState<'calls' | 'sentiment' | 'conversion'>('calls');

  const isAdmin = adminUser?.role === 'Admin';
  const dashboardUserId = isAdmin ? undefined : adminUser?.id;

  // Use the separated hook, passing the days filter
  const { summary, isSummaryLoading, recentSessions } = useDashboardData(dashboardUserId, days);

  // Fetch integration statuses
  const { data: connectionsData } = useGetConnectionsQuery();
  const { data: crmStatusData } = useGetCrmStatusQuery();

  const calendarConnections = connectionsData?.data || [];
  const sfStatus = crmStatusData?.data?.salesforce;

  const isGoogleConnected = calendarConnections.some((c: any) => c.provider === 'google');
  const isOutlookConnected = calendarConnections.some((c: any) => c.provider === 'outlook');
  const isSalesforceConnected = sfStatus?.connected ?? false;
  const isHubspotConnected = localStorage.getItem('finnai_hubspot_connected') === 'true';
  const totalConnected = [isGoogleConnected, isOutlookConnected, isSalesforceConnected, isHubspotConnected].filter(Boolean).length;

  const orgStats = [
    { label: 'Total Meetings', value: isSummaryLoading ? <DashboardStatSkeleton /> : summary?.total_sessions?.toString() || '0', sub: `Over last ${days} days`, icon: PhoneCall, badgeText: '', badgeColor: 'transparent' },
    { label: 'Meeting Duration', value: isSummaryLoading ? <DashboardStatSkeleton /> : `${((summary?.total_duration_seconds || 0) / 3600).toFixed(1)}h`, sub: `Over last ${days} days`, icon: Clock, badgeText: '', badgeColor: 'transparent' },
    { label: 'Avg. Sentiment', value: isSummaryLoading ? <DashboardStatSkeleton /> : (summary?.avg_sentiment_score ? (summary.avg_sentiment_score > 0 ? '+' : '') + summary.avg_sentiment_score.toFixed(1) : 'N/A'), sub: `Over last ${days} days`, icon: Activity, badgeText: '', badgeColor: 'transparent' },
    { label: 'Battlecard Usage', value: isSummaryLoading ? <DashboardStatSkeleton /> : (summary?.battlecard_usage_rate != null ? `${summary.battlecard_usage_rate}%` : 'N/A'), sub: `Over last ${days} days`, icon: Zap, badgeText: '', badgeColor: 'transparent' },
  ];

  const integrations = [
    { name: 'Google Calendar', icon: CalendarDays, status: isGoogleConnected ? 'connected' : 'not_connected', desc: 'Meetings & scheduling' },
    { name: 'Outlook Calendar', icon: CalendarDays, status: isOutlookConnected ? 'connected' : 'not_connected', desc: 'Meetings & scheduling' },
    { name: 'Salesforce', icon: Database, status: isSalesforceConnected ? 'connected' : 'not_connected', desc: 'CRM synchronization' },
  ];

  const teamActivity = recentSessions.map((session: any) => ({
    id: session.id,
    rep: session.owner?.name || 'Rep',
    prospect: session.meetingTitle || 'Unnamed Meeting',
    date: session.startedAt ? formatDate(session.startedAt) : 'Unknown',
    duration: session.durationSeconds ? `${Math.floor(session.durationSeconds / 60)}m ${session.durationSeconds % 60}s` : '0m',
    platform: session.platform || 'unknown'
  }));

  const [triggerExport, { isFetching: isExporting }] = useLazyListSessionsQuery();

  const handleExport = async () => {
    try {
      const result = await triggerExport({ limit: 1000, days, user_id: dashboardUserId }).unwrap();
      const allSessions = result.data.data;
      
      if (!allSessions || allSessions.length === 0) {
        AlertUtils.toast("No activity data to export.", 'warning');
        return;
      }

      const headers = [
        "Session ID", "Date", "Meeting Title", "Platform", "Duration (sec)", "Status",
        "Sentiment Score", "Engagement Score", "Buying Intent", "Intent Score",
        "Battlecards Generated", "Battlecards Used", "Objections", "Nudges",
        "Transcript Words", "Topics Covered"
      ];
      
      const rows = allSessions.map((session: any) => [
        `"${session.id}"`,
        `"${session.startedAt ? formatDate(session.startedAt) : 'Unknown'}"`,
        `"${session.meetingTitle || 'Unnamed Meeting'}"`,
        `"${session.platform || 'Unknown'}"`,
        session.durationSeconds || 0,
        `"${session.status || 'Unknown'}"`,
        session.finalSentimentScore || 0,
        session.finalEngagementScore || 0,
        `"${session.finalBuyingIntentLabel || 'N/A'}"`,
        session.finalBuyingIntentScore || 0,
        session.battlecardsGenerated || 0,
        session.battlecardsUsed || 0,
        session.objectionsDetected || 0,
        session.nudgesShown || 0,
        session.transcriptWordCount || 0,
        `"${(session.topicsCovered || []).join('; ')}"`
      ]);

      const csvContent = [headers.join(","), ...rows.map((row: any) => row.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `finnai_full_analytics_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      AlertUtils.toast("Export successful!", 'success');
    } catch (error) {
      console.error("Export failed:", error);
      AlertUtils.toast("Failed to generate export file.", 'error');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ color: 'var(--admin-card-text)', margin: 0, fontFamily: 'var(--admin-font-primary)', fontWeight: 700, fontStyle: 'normal', fontSize: '37.7px', lineHeight: '38px', letterSpacing: '-0.76px', verticalAlign: 'middle' }}>
            {isAdmin ? 'Workspace Overview' : 'My Performance'}
          </h1>
          <p className="text-xs font-normal" style={{ color: 'var(--admin-card-subtext)', margin: '8px 0 0' }}>
            {isAdmin ? "Your organization's AI co-pilot performance at a glance." : "Your AI co-pilot performance at a glance."}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Dropdown
            value={days.toString()}
            onChange={(val) => setDays(Number(val))}
            options={[
              { value: '7', label: 'Last 7 days' },
              { value: '30', label: 'Last 30 days' },
              { value: '60', label: 'Last 60 days' },
            ]}
            style={{ width: '130px', backgroundColor: 'var(--color-dropdown-bg, #09090B)' }}
          />
          <button
            onClick={handleExport}
            disabled={isExporting}
            style={{
              height: 'auto',
              borderRadius: '0',
              padding: '6px 14px',
              fontSize: '12px',
              fontWeight: 700,
              backgroundColor: 'var(--admin-sidebar-logo-text, #ffffff)',
              color: 'var(--admin-bg-container, #010204)',
              border: '1px solid var(--admin-sidebar-logo-text, #ffffff)',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              opacity: isExporting ? 0.7 : 1
            }}
            className="hover:opacity-90"
          >
            {isExporting ? 'Exporting...' : 'Export report'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        {orgStats.map((s, i) => <StatCard key={s.label} {...s} index={i} />)}
      </div>

      <ExtensionSetupGuide index={3} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px' }}>
        <SectionCard
          title={isAdmin ? "Team Activity" : "My Activity"}
          sub={isAdmin ? "Most recent calls across your workspace" : "Most recent calls across your account"}
          rightAction={<span onClick={() => navigate('/admin/calls')} style={{ color: 'var(--admin-card-subtext)', fontSize: '11px', textDecoration: 'none', cursor: 'pointer' }}>View all <ArrowUpRight size={10} style={{ display: 'inline', verticalAlign: 'middle' }} /></span>}
          index={4}
        >
          {teamActivity.map((call: any, idx: number) => (
            <div
              key={idx}
              onClick={() => navigate(`/admin/sessions/${call.id}`)}
              style={{ cursor: 'pointer', padding: '12px 16px', borderBottom: '1px solid var(--admin-card-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              className="hover:bg-white/5 transition-colors"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: 'var(--color-glass-surface, rgba(255,255,255,0.05))', border: '1px solid var(--admin-card-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {getPlatformLogo(call.platform)}
                </div>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--admin-card-text)', margin: '0 0 2px 0' }}>{call.prospect}</p>
                  <p style={{ fontSize: '11px', color: 'var(--admin-card-subtext)', margin: 0 }}>{call.rep} • {call.date}</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '12px', color: 'var(--admin-card-subtext)' }}>{call.duration}</span>
                <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '0', background: 'var(--admin-accent-green-dim)', color: 'var(--admin-accent-green)', textTransform: 'uppercase' }}>DONE</span>
              </div>
            </div>
          ))}
          {teamActivity.length === 0 && (
            <div style={{ padding: '24px 16px', textAlign: 'center' }}>
              <p style={{ fontSize: '12px', color: 'var(--admin-card-subtext)', margin: 0 }}>No older calls yet - activity will appear here as your team runs meetings.</p>
            </div>
          )}
        </SectionCard>

        <SectionCard
          title="Integrations"
          rightAction={<span style={{ color: 'var(--admin-card-subtext)', fontSize: '10px', fontWeight: 600 }}>{totalConnected}/4 CONNECTED</span>}
          index={5}
        >
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {integrations.map((integration, idx) => (
              <div key={idx} style={{ padding: '12px', borderRadius: '6px', border: '1px solid var(--admin-card-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '4px', background: integration.status === 'connected' ? 'var(--admin-accent-green-dim)' : 'var(--color-glass-surface, rgba(255,255,255,0.05))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <integration.icon size={14} color={integration.status === 'connected' ? 'var(--admin-accent-green)' : 'var(--admin-card-text)'} />
                  </div>
                  <div>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--admin-card-text)', margin: 0 }}>{integration.name}</p>
                    <p style={{ fontSize: '11px', color: 'var(--admin-card-subtext)', margin: '1px 0 0' }}>{integration.desc}</p>
                  </div>
                </div>
                {integration.status === 'connected' && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--admin-accent-green)' }}></div>}
              </div>
            ))}
            <div style={{ textAlign: 'center', marginTop: '6px' }}>
              <span 
                onClick={() => navigate('/admin/integrations')} 
                style={{ fontSize: '12px', fontWeight: 500, color: 'var(--admin-accent-green)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
                className="hover:opacity-80 transition-opacity"
              >
                View all <ArrowUpRight size={12} />
              </span>
            </div>
          </div>
        </SectionCard>
      </div>

      <SectionCard
        title={isAdmin ? "Team Usage & Productivity" : "My Usage & Productivity"}
        sub={isAdmin ? "Per-rep call volume, productivity trends, and conversion metrics" : "Call volume, productivity trends, and conversion metrics"}
        rightAction={
          <div style={{ display: 'flex', border: '1px solid var(--admin-card-border)', borderRadius: '0', overflow: 'hidden' }}>
            <button onClick={() => setChartTab('calls')} style={{ padding: '4px 10px', fontSize: '10px', background: chartTab === 'calls' ? 'var(--color-glass-surface, rgba(255,255,255,0.1))' : 'transparent', color: chartTab === 'calls' ? 'var(--admin-card-text)' : 'var(--admin-card-subtext)', border: 'none', cursor: 'pointer' }}>Volume</button>
            <button onClick={() => setChartTab('sentiment')} style={{ padding: '4px 10px', fontSize: '10px', background: chartTab === 'sentiment' ? 'var(--color-glass-surface, rgba(255,255,255,0.1))' : 'transparent', color: chartTab === 'sentiment' ? 'var(--admin-card-text)' : 'var(--admin-card-subtext)', border: 'none', cursor: 'pointer', borderLeft: '1px solid var(--admin-card-border)' }}>Sentiment</button>
            <button onClick={() => setChartTab('conversion')} style={{ padding: '4px 10px', fontSize: '10px', background: chartTab === 'conversion' ? 'var(--color-glass-surface, rgba(255,255,255,0.1))' : 'transparent', color: chartTab === 'conversion' ? 'var(--admin-card-text)' : 'var(--admin-card-subtext)', border: 'none', cursor: 'pointer', borderLeft: '1px solid var(--admin-card-border)' }}>Conversion</button>
          </div>
        }
        index={6}
      >
        <div style={{ height: '240px', width: '100%', padding: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {isSummaryLoading ? (
            <DashboardChartSkeleton />
          ) : (!summary?.daily_calls || summary.daily_calls.length === 0) ? (
            <EmptyState
              icon={BarChart3}
              title="No Activity Data"
              desc="There is no call volume data available for the selected time period. Data will appear here once calls are processed."
            />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={summary.daily_calls} margin={{ top: 10, right: 20, left: 40, bottom: 40 }}>
                <defs>
                  <linearGradient id="chart-gradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-chart-green, #74DD66)" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="var(--color-chart-green-dark, #011F01)" stopOpacity="0.1" />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" stroke="var(--admin-card-subtext)" fontSize={11} tickLine={false} axisLine={false} tickMargin={8} label={{ value: `Date (Last ${days} days)`, position: 'insideBottom', offset: -30, fill: 'var(--admin-card-subtext)', fontSize: 11 }} />
                <YAxis stroke="var(--admin-card-subtext)" fontSize={11} tickLine={false} axisLine={false} tickMargin={8} label={{ value: chartTab === 'calls' ? 'Volume (Calls)' : chartTab === 'sentiment' ? 'Sentiment Score' : 'Conversion Intent', angle: -90, position: 'insideLeft', offset: -30, fill: 'var(--admin-card-subtext)', fontSize: 11 }} />
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle, rgba(255,255,255,0.05))" vertical={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'var(--admin-card-bg)', border: '1px solid var(--admin-card-border)', borderRadius: '0' }}
                  itemStyle={{ color: 'var(--admin-card-text)', fontSize: '12px' }}
                  labelStyle={{ color: 'var(--admin-card-subtext)', fontSize: '12px' }}
                />
                <Area
                  type="monotone"
                  dataKey={chartTab}
                  stroke="var(--color-chart-green, #74DD66)"
                  strokeWidth={2}
                  fill="url(#chart-gradient)"
                  activeDot={{ r: 4, fill: 'var(--color-chart-green, #74DD66)', stroke: 'var(--color-text-primary, #fff)', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
